import './polyfills';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App.tsx';
import { configureAmplify } from '@/config/amplify';
import config from '@/config';
import { apiErrorBeforeSend } from '@/utils/sentryApiErrors';

configureAmplify();

// Browser/network noise that isn't an app bug — matched regardless of which layer captured it,
// since these can surface from fetches, streams, or the global handlers alike.
const IGNORED_ERROR_PATTERNS = [
  /ResizeObserver loop/i,
  /Loading chunk [\d]+ failed/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /Network ?Error/i,
  /Failed to fetch/i,
  /Load failed/i,
  /AbortError/i,
  // Cross-origin script errors with the detail stripped by the browser (CORS) — near-always a
  // third-party/extension script, not our code, and gives nothing actionable to debug from.
  /^Script error\.?$/i,
  // Sentry's own label for a thrown/rejected non-Error value (e.g. a bare string or plain
  // object) — usually from a third-party script, not something our error boundaries throw.
  /Non-Error (promise rejection|exception) captured/i,
];

Sentry.init({
  dsn: config.sentryDsn,
  environment: config.appEnv,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  // Capture 10% of transactions in production, 100% otherwise
  tracesSampleRate: config.appEnv === 'production' ? 0.1 : 1.0,
  // Capture replays only on errors in production
  replaysOnErrorSampleRate: 1.0,
  replaysSessionSampleRate: config.appEnv === 'production' ? 0.1 : 0,
  enabled: !!config.sentryDsn,
  beforeSend(event, hint) {
    const message =
      event.message ??
      event.exception?.values?.[0]?.value ??
      (hint.originalException as Error | undefined)?.message ??
      '';
    if (IGNORED_ERROR_PATTERNS.some(pattern => pattern.test(message))) return null;
    return apiErrorBeforeSend(event, hint);
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
